<script lang="ts">
  import { pageTitle } from '../../lib/pageTitle';
  import { onMount } from 'svelte';
  import { api, type StoreCategory, type StoreProduct } from '../../lib/api';
  import { cart } from '../../lib/cart';
  import type { CartProduct } from '../../lib/cart';
  import { trackAddToCart } from '#lib/analytics.ts';

  let products: StoreProduct[] = [];
  let categories: StoreCategory[] = [];
  let selectedCategory = '';
  let loading = true;
  let addedProductId: number | null = null;

  async function loadProducts() {
    loading = true;
    try {
      const [prods, cats] = await Promise.all([
        api.getProducts(selectedCategory || undefined),
        api.getCategories(),
      ]);
      products = prods;
      categories = cats;
    } catch (err) {
      console.error('Failed to load products', err);
    } finally {
      loading = false;
    }
  }

  function selectCategory(slug: string) {
    selectedCategory = slug;
    loadProducts();
  }

  function formatPrice(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  function addToCart(product: StoreProduct) {
    const cartProduct: CartProduct = {
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      image_url: product.image_url,
      quantity_available: product.quantity,
    };
    cart.addItem(cartProduct);
    trackAddToCart({ id: product.id, name: product.name, priceCents: product.price, quantity: 1 });
    addedProductId = product.id;
    window.setTimeout(() => {
      addedProductId = null;
    }, 1500);
  }

  onMount(loadProducts);
</script>

<svelte:head>
  <title>{pageTitle('Store')}</title>
</svelte:head>

<div class="store-page">
  <div class="store-header">
    <div class="container text-center pb-5">
      <h1 class="text-uppercase">SpinFlip Store</h1>
      <p class="lead store-lead">Quality products from our engineering lab to your doorstep.</p>
    </div>
  </div>

  <div class="container py-4 store-body">
    {#if categories.length > 0}
      <div class="category-filter mb-4">
        <button
          class="btn {selectedCategory === '' ? 'btn-primary' : 'btn-outline-secondary'} me-2 mb-2"
          on:click={() => selectCategory('')}
        >
          All
        </button>
        {#each categories as cat}
          <button
            class="btn {selectedCategory === cat.slug ? 'btn-primary' : 'btn-outline-secondary'} me-2 mb-2"
            on:click={() => selectCategory(cat.slug)}
          >
            {cat.name}
          </button>
        {/each}
      </div>
    {/if}

    {#if loading}
      <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    {:else if products.length === 0}
      <div class="text-center py-5">
        <p class="text-muted fs-5">No products available at this time. Check back soon!</p>
      </div>
    {:else}
      <div class="row">
        {#each products as product}
          <div class="col-sm-6 col-md-4 col-lg-3 mb-4">
            <div class="card product-card h-100">
              <a href="/store/{product.slug}" class="card-img-link">
                {#if product.image_url}
                  <img src={product.image_url} class="card-img-top" alt={product.name} />
                {:else}
                  <div class="card-img-placeholder">
                    <i class="fas fa-box-open fa-3x"></i>
                  </div>
                {/if}
              </a>
              <div class="card-body d-flex flex-column">
                <a href="/store/{product.slug}" class="product-link">
                  <h5 class="card-title">{product.name}</h5>
                </a>
                {#if product.category}
                  <span class="badge bg-secondary mb-2">{product.category.name}</span>
                {/if}
                <p class="card-text text-muted small flex-grow-1">
                  {product.description.length > 100
                    ? product.description.substring(0, 100) + '...'
                    : product.description}
                </p>
                <div class="d-flex justify-content-between align-items-center mt-auto">
                  <span class="price">{formatPrice(product.price)}</span>
                  {#if product.quantity > 0}
                    <button
                      class="btn btn-sm {addedProductId === product.id ? 'btn-success' : 'btn-primary'}"
                      on:click={() => addToCart(product)}
                    >
                      {addedProductId === product.id ? 'Added!' : 'Add to Cart'}
                    </button>
                  {:else}
                    <span class="badge bg-danger">Out of Stock</span>
                  {/if}
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .store-page {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Pull dark hero under main's nav offset so no white band (main padding is white) */
  .store-header {
    margin-top: calc(-1 * var(--site-header-offset));
    padding-top: calc(var(--site-header-offset) + 2rem);
    background-color: #212529;
    color: #fff;
  }

  .store-header h1 {
    color: #fed136;
  }

  .store-lead {
    color: rgba(255, 255, 255, 0.75);
  }

  .product-card {
    border: 1px solid #e0e0e0;
    transition: box-shadow 0.3s ease;
  }

  .product-card:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }

  .card-img-top {
    height: 200px;
    object-fit: cover;
  }

  .card-img-placeholder {
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8f9fa;
    color: #adb5bd;
  }

  .card-img-link {
    text-decoration: none;
  }

  .product-link {
    text-decoration: none;
    color: inherit;
  }

  .product-link:hover {
    color: var(--bs-link-hover-color);
  }

  .price {
    font-size: 1.2rem;
    font-weight: 700;
    color: #212529;
  }

  .category-filter {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 1rem;
  }

  .store-body {
    flex: 1 1 auto;
  }
</style>
